import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBillingInfo } from '~/billing/hooks/useBillingInfo'
import { Button } from '~/components/common/Button'
import { Card } from '~/components/common/Card'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import { Typography } from '~/components/common/Typography'
import { useBillingAccounts } from '~/contexts/BillingAccountsContext'
import { getBillingAccountWorkspaces } from '~/services/organizationService'
import { bridge } from '~/services/bridge'
import { BetterToast } from '~/utils'
import { selectWorkspace, storeWorkspaceSelection } from '~/utils/workspaceSelection'

interface AccountDialogProps {
  isOpen?: boolean
  onClose: () => void
}

export function AccountDialog({ isOpen, onClose }: AccountDialogProps) {
  const isPlugin = bridge.isInPlugin()
  const { billingAccounts, loaded, error, setSelectedBillingAccountId } = useBillingAccounts()
  const billingInfo = useBillingInfo()
  const navigate = useNavigate()
  const [switchingAccountId, setSwitchingAccountId] = useState<number | null>(null)

  const handleSelectAccount = async (billingAccount: { id: number; name: string }) => {
    if (switchingAccountId !== null) return

    setSwitchingAccountId(billingAccount.id)
    try {
      const workspaces = await getBillingAccountWorkspaces(billingAccount.id)
      const selectedWorkspace = selectWorkspace(billingAccount.id, workspaces, undefined)

      if (selectedWorkspace) {
        storeWorkspaceSelection(billingAccount.id, selectedWorkspace.uuid)
      }

      setSelectedBillingAccountId(billingAccount.id)

      if (selectedWorkspace) {
        navigate(`/w/${selectedWorkspace.uuid}`, { replace: true })
      }

      BetterToast.success(`Selected ${billingAccount.name}`)
      onClose()
    } catch (err) {
      BetterToast.error(err instanceof Error ? err.message : 'Failed to switch billing account')
    } finally {
      setSwitchingAccountId(null)
    }
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={
        isPlugin ? 'w-full max-w-[calc(100vw-24px)] h-[calc(100vh-32px)]' : 'w-[548px] h-[622px]'
      }
    >
      <div className='flex flex-col h-full'>
        {/* Header */}
        <div className='flex py-2 pl-6 justify-between items-center self-stretch border-b border-[#EAEAEA]'>
          <div className='flex h-9 items-center gap-4 flex-1'>
            <div className='flex flex-col justify-center items-start flex-1'>
              <Typography
                fontFamily='geist'
                fontSize='titleSm'
                fontWeight='bold'
                className='text-black'
              >
                Select an Account
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='medium'
                className='text-[rgba(0,0,0,0.65)]'
              >
                {billingInfo?.name}
              </Typography>
            </div>
          </div>
          <Button
            variant='borderless'
            onClick={onClose}
            className='h-full flex p-5 items-center gap-2 hover:bg-transparent hover:opacity-100'
          >
            <span className='w-6 h-6'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M19.281 18.2194C19.3507 18.2891 19.406 18.3718 19.4437 18.4629C19.4814 18.5539 19.5008 18.6515 19.5008 18.7501C19.5008 18.8486 19.4814 18.9462 19.4437 19.0372C19.406 19.1283 19.3507 19.211 19.281 19.2807C19.2114 19.3504 19.1286 19.4056 19.0376 19.4433C18.9465 19.4811 18.849 19.5005 18.7504 19.5005C18.6519 19.5005 18.5543 19.4811 18.4632 19.4433C18.3722 19.4056 18.2895 19.3504 18.2198 19.2807L12.0004 13.0604L5.78104 19.2807C5.64031 19.4214 5.44944 19.5005 5.25042 19.5005C5.05139 19.5005 4.86052 19.4214 4.71979 19.2807C4.57906 19.1399 4.5 18.9491 4.5 18.7501C4.5 18.551 4.57906 18.3602 4.71979 18.2194L10.9401 12.0001L4.71979 5.78068C4.57906 5.63995 4.5 5.44907 4.5 5.25005C4.5 5.05103 4.57906 4.86016 4.71979 4.71943C4.86052 4.5787 5.05139 4.49963 5.25042 4.49963C5.44944 4.49963 5.64031 4.5787 5.78104 4.71943L12.0004 10.9397L18.2198 4.71943C18.3605 4.5787 18.5514 4.49963 18.7504 4.49963C18.9494 4.49963 19.1403 4.5787 19.281 4.71943C19.4218 4.86016 19.5008 5.05103 19.5008 5.25005C19.5008 5.44907 19.4218 5.63995 19.281 5.78068L13.0607 12.0001L19.281 18.2194Z'
                  fill='#262626'
                />
              </svg>
            </span>
          </Button>
        </div>

        {/* Body */}
        <ModalBody
          className={`flex-1 overflow-y-auto ${isPlugin ? 'px-3 py-4' : 'px-[74px] py-[48px]'}`}
        >
          {!loaded ? (
            <div className='text-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto'></div>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-[rgba(0,0,0,0.65)] mt-2'
              >
                Loading accounts...
              </Typography>
            </div>
          ) : error ? (
            <div className='text-center py-8'>
              <Typography fontFamily='geist' fontSize='bodySm' className='text-[#D32F2F]'>
                {error.message}
              </Typography>
            </div>
          ) : billingAccounts && billingAccounts.length > 0 ? (
            <div
              className={`flex flex-col ${isPlugin ? 'items-stretch space-y-3' : 'items-center space-y-6'}`}
            >
              {billingAccounts.map(billingAccount => (
                <Card
                  key={billingAccount.id}
                  title={billingAccount.name}
                  className={isPlugin ? 'w-full h-auto min-h-[64px] px-4 py-3' : undefined}
                  tag={
                    billingAccount.is_billing_admin
                      ? {
                          text: 'Admin',
                          variant: 'success'
                        }
                      : undefined
                  }
                  action={
                    <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-400'>
                      ›
                    </Typography>
                  }
                  onClick={() => void handleSelectAccount(billingAccount)}
                />
              ))}
            </div>
          ) : (
            <div className='text-center py-8'>
              <Typography fontFamily='geist' fontSize='bodySm' className='text-[rgba(0,0,0,0.65)]'>
                No accounts found. Please contact your administrator.
              </Typography>
            </div>
          )}
        </ModalBody>

        {/* Footer */}
        <ModalFooter
          className={`mt-auto flex flex-col justify-start items-start gap-2 flex-shrink-0 border-t border-gray-200 ${isPlugin ? 'px-3 py-3' : 'h-[78px] px-[32px] py-[16px]'}`}
        >
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='medium'
            className='text-[rgba(0,0,0,0.45)]'
            style={{
              letterSpacing: '0.1px'
            }}
          >
            You&apos;re a member of multiple accounts with different roles. You can switch between
            accounts at any time.
          </Typography>
        </ModalFooter>
      </div>
    </Modal>
  )
}

export default AccountDialog
